import { School } from "@mui/icons-material";
import {
    Avatar,
    Box,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    styled,
    Typography,
} from "@mui/material";
import { api, types } from "api";
import { InfoSnackBar } from "components/InfoSnackBar";
import { PasswordValidation } from "components/PasswordValidation";
import { SignUpTextField } from "components/SignUpTextField";
import { StyledButton } from "components/StyledButton";
import { isValidEmail, passwordAcceptable } from "helpers/authValidation";
import { useAuthStatus } from "helpers/useAuthStatus";
import {
    DUPLICATE_EMAIL,
    ERR_REGISTER,
} from "messages";
import React, { useCallback, useRef, useState } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { routes } from "routes";
import { useKeycloak } from "@react-keycloak/web";

const PREFIX = "RegisterPageForm";

const classes = {
    root: `${PREFIX}-root`,
    avatar: `${PREFIX}-avatar`,
    avatarIcon: `${PREFIX}-avatarIcon`,
    form: `${PREFIX}-form`,
    formArea: `${PREFIX}-formArea`,
    heading: `${PREFIX}-heading`,
    label: `${PREFIX}-label`,
    link: `${PREFIX}-link`,
    footnote: `${PREFIX}-footnote`,
    registerButton: `${PREFIX}-registerButton`,
    googleButton: `${PREFIX}-googleButton`,
};

const StyledFormPanel = styled(Box)(({ theme }) => ({
    [`&.${classes.root}`]: {
        width: "100%",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },

    // Flexible area that keeps the form content vertically centred and
    // pushes the footer SVG to the absolute bottom of the panel
    [`& .${classes.formArea}`]: {
        width: "100%",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },

    [`& .${classes.avatar}`]: {
        width: 56,
        height: 56,
        marginBottom: theme.spacing(1.5),
        backgroundColor: "#FFFFFF",
        color: "#1E3A8A",
    },

    [`& .${classes.avatarIcon}`]: {
        width: 32,
        height: 32,
    },

    [`& .${classes.heading}`]: {
        color: "#FFFFFF",
        marginBottom: theme.spacing(1),
    },

    [`& .${classes.form}`]: {
        width: "100%",
        maxWidth: "520px",
        marginTop: theme.spacing(2),
    },

    [`& .${classes.label}`]: {
        alignSelf: "start",
        paddingTop: theme.spacing(2),
        textAlign: "left",
    },

    [`& .${classes.link}`]: {
        paddingLeft: theme.spacing(0.5),
        color: "#FFFFFF",
        fontWeight: 600,
        "&:hover": {
            color: "rgba(255, 255, 255, 0.85)",
        },
    },

    [`& .${classes.footnote}`]: {
        color: "rgba(255, 255, 255, 0.9)",
        marginTop: theme.spacing(1),
    },

    [`& .${classes.registerButton}.MuiButton-containedPrimary`]: {
        backgroundColor: "#FFFFFF",
        color: "#0B1C3F",
        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
        "&:hover": {
            backgroundColor: "#E3EBFB",
            boxShadow: "0 6px 18px rgba(0, 0, 0, 0.3)",
        },
    },

    [`& .${classes.googleButton}.MuiButton-outlinedPrimary`]: {
        marginTop: theme.spacing(1.5),
        color: "#FFFFFF",
        borderColor: "rgba(255, 255, 255, 0.6)",
        "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
            borderColor: "#FFFFFF",
        },
    },

    // Invert text colours so the form stays legible on the dark blue panel
    "& .MuiTypography-root": {
        color: "#FFFFFF",
    },

    "& .MuiFormLabel-root": {
        color: "rgba(255, 255, 255, 0.9)",
    },

    "& .MuiFormLabel-root.Mui-focused": {
        color: "#FFFFFF",
    },

    "& .MuiFormControlLabel-label": {
        color: "#FFFFFF",
    },

    "& .MuiRadio-root": {
        color: "rgba(255, 255, 255, 0.85)",
        "&.Mui-checked": {
            color: "#FFFFFF",
        },
    },

    "& .MuiFormHelperText-root.Mui-error": {
        color: "#FFB4AB",
    },
}));

export interface RegisterPageFormState extends types.RegisterAdmin {
    confirmPassword: string;
}

export const RegisterPageForm = (): JSX.Element => {
    const history = useHistory();
    const { setRole } = useAuthStatus();
    const { keycloak } = useKeycloak();

    const startGoogleTeacherRegister = useCallback((): void => {
        const redirectUri = new URL(routes._.signIn.path, window.location.origin).toString();
        window.history.replaceState(null, document.title, routes._.signIn.path);
        void keycloak.login({ redirectUri, idpHint: "google" });
    }, [keycloak]);

    const [state, setState] = useState<RegisterPageFormState>({
        firstName: "",
        lastName: "",
        email: "",
        license: "teacher",
        schoolName: "",
        password: "",
        confirmPassword: "",
        role: ["teacher"],
    });

    const [registerFailed, setRegisterFailed] = useState(false);

    const [firstNameError, setFirstNameError] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [schoolNameError, setSchoolNameError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");

    const checkIsFormValid = useCallback(() => {
        if (state.firstName.trim().length === 0) {
            setFirstNameError("First name is required");
            return false;
        } else {
            setFirstNameError("");
        }

        if (state.lastName.trim().length === 0) {
            setLastNameError("Last name is required");
            return false;
        } else {
            setLastNameError("");
        }

        if (state.email.trim().length === 0) {
            setEmailError("Email is required");
            return false;
        } else {
            setEmailError("");
        }

        // Validate the email
        if (!isValidEmail(state.email)) {
            setEmailError("Invalid email");
            return false;
        } else {
            setEmailError("");
        }

        if (state.schoolName.trim().length === 0) {
            setSchoolNameError("School name is required");
            return false;
        } else {
            setSchoolNameError("");
        }

        if (state.password.trim().length === 0) {
            setPasswordError("Password is required");
            return false;
        } else {
            setPasswordError("");
        }

        if (state.confirmPassword.trim().length === 0) {
            setConfirmPasswordError("A confirmation password is required");
            return false;
        } else {
            setConfirmPasswordError("");
        }

        if (state.password !== state.confirmPassword) {
            setConfirmPasswordError("Passwords do not match");
            return false;
        } else if (!passwordAcceptable(state.password)) {
            setPasswordError("Invalid password");
            return false;
        }

        return true;
    }, [
        state.firstName,
        state.lastName,
        state.email,
        state.schoolName,
        state.password,
        state.confirmPassword,
    ]);

    const message = useRef(ERR_REGISTER);

    const handleSubmit = (event: React.FormEvent): void => {
        event.preventDefault();

        if (!checkIsFormValid()) {
            return;
        }

        const { confirmPassword, ...payload } = state;
        api
            .registerAdminNewSchool(payload)
            .then((res) => {
                if (res.status === 409) {
                    message.current =
                        state.license === "teacher"
                            ? "School not found. A school administrator must register first."
                            : DUPLICATE_EMAIL;
                    setRegisterFailed(true);
                    return;
                }
                if (res.status !== 201) {
                    setRegisterFailed(true);
                    return;
                }

                api
                    .authStatus()
                    .then((authStatus) => {
                        if (authStatus.status === 200) {
                            setRole(authStatus.data.role);
                        }
                        history.replace(routes.path);
                    })
                    .catch(() => {
                        history.replace(routes.path);
                    });
            })
            .catch(() => {
                message.current = ERR_REGISTER;
                setRegisterFailed(true);
            });
    };

    const handleValueChange =
        (field: keyof RegisterPageFormState) =>
        (
            event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
        ): void => {
            setState((currState) => ({
                ...currState,
                [field]: event.target.value,
            }));
        };

    const handleTeacherRoleChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        setState({
            ...state,
            role: [event.target.value as types.RoleType],
        });
    };

    return (
        <StyledFormPanel className={classes.root}>
            <Box className={classes.formArea}>
                <Avatar className={classes.avatar}>
                    <School className={classes.avatarIcon} />
                </Avatar>
                <Typography component="h5" variant="h5" className={classes.heading}>
                    Sign Up
                </Typography>
                <form onSubmit={handleSubmit} className={classes.form}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <SignUpTextField
                                autoFocus
                                autoComplete="name"
                                name="firstName"
                                id="firstName"
                                error={firstNameError !== ""}
                                helperText={firstNameError}
                                label="First Name"
                                dataTestId="firstNameInput"
                                onChange={handleValueChange("firstName")}
                                value={state.firstName}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SignUpTextField
                                id="lastName"
                                label="Last Name"
                                name="lastName"
                                error={lastNameError !== ""}
                                helperText={lastNameError}
                                autoComplete="lname"
                                dataTestId="lastNameInput"
                                onChange={handleValueChange("lastName")}
                                value={state.lastName}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <SignUpTextField
                                id="email"
                                label="Email Address"
                                name="email"
                                error={emailError !== ""}
                                helperText={emailError}
                                autoComplete="email"
                                dataTestId="emailInput"
                                onChange={handleValueChange("email")}
                                value={state.email}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl
                                fullWidth
                                variant="outlined"
                                error={schoolNameError !== ""}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "32px",
                                        backgroundColor: "#FFFFFF",
                                        boxShadow: "inset 0 0 0 1px rgba(9, 32, 63, 0.14)",
                                        transition: "box-shadow 0.2s ease-in-out",
                                        "&:hover": {
                                            boxShadow: "inset 0 0 0 1px rgba(9, 32, 63, 0.35)",
                                        },
                                        "&.Mui-focused": {
                                            boxShadow: "inset 0 0 0 2px #2A5EFF",
                                        },
                                        "&.Mui-error": {
                                            boxShadow: "inset 0 0 0 2px #D32F2F",
                                        },
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        border: "none",
                                    },
                                    "& .MuiInputLabel-outlined": {
                                        position: "static",
                                        transform: "none",
                                        marginBottom: "6px",
                                        textAlign: "left",
                                        color: "rgba(255, 255, 255, 0.92)",
                                        "&.MuiInputLabel-shrink": {
                                            transform: "none",
                                        },
                                        "&.Mui-focused": {
                                            color: "#FFFFFF",
                                            fontWeight: 600,
                                        },
                                        "&.Mui-error": {
                                            color: "#FFB4AB",
                                        },
                                    },
                                    "& .MuiSelect-select": {
                                        color: "#0B1C3F",
                                        paddingTop: "14px",
                                        paddingBottom: "14px",
                                    },
                                    "& .MuiSelect-icon": {
                                        color: "#0B1C3F",
                                    },
                                }}
                            >
                                <InputLabel id="school-select-label" shrink>
                                    School Name
                                </InputLabel>
                                <Select
                                    labelId="school-select-label"
                                    id="schoolName"
                                    name="schoolName"
                                    value={state.schoolName}
                                    onChange={handleValueChange("schoolName") as any}
                                    label="School Name"
                                    data-testid="schoolNameInput"
                                >
                                    <MenuItem value="Swinburne University">
                                        Swinburne University
                                    </MenuItem>
                                    <MenuItem value="Monash University">
                                        Monash University
                                    </MenuItem>
                                    <MenuItem value="RMIT University">RMIT University</MenuItem>
                                    <MenuItem value="Victoria University">
                                        Victoria University
                                    </MenuItem>
                                </Select>
                                {schoolNameError !== "" && (
                                    <FormHelperText>{schoolNameError}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SignUpTextField
                                name="password"
                                label="Password"
                                type="password"
                                id="password"
                                error={passwordError !== ""}
                                helperText={passwordError}
                                dataTestId="passInput"
                                autoComplete="current-password"
                                onChange={handleValueChange("password")}
                                value={state.password}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SignUpTextField
                                name="Confirm password"
                                label="Confirm Password"
                                type="password"
                                id="Confirm password"
                                error={confirmPasswordError !== ""}
                                helperText={confirmPasswordError}
                                dataTestId="confirmPassInput"
                                onChange={handleValueChange("confirmPassword")}
                                value={state.confirmPassword}
                            />
                        </Grid>
                    </Grid>
                    <PasswordValidation
                        password={state.password}
                        confirmPassword={state.confirmPassword}
                    />
                    <Grid container alignContent="flex-start" alignItems="flex-start">
                        <FormControl component="fieldset">
                            <FormLabel className={classes.label}>Teacher Access</FormLabel>
                            <RadioGroup
                                aria-label="teacher-role"
                                name="teacher-role"
                                value={state.role[0]}
                                onChange={handleTeacherRoleChange}
                                row
                            >
                                <FormControlLabel
                                    value="teacher"
                                    control={<Radio />}
                                    label="Regular Teacher"
                                />
                                <FormControlLabel
                                    value="admin-teacher"
                                    control={<Radio />}
                                    label="Admin Teacher"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Grid container alignContent="center" direction="column">
                        <Grid item xs={6}>
                            <StyledButton
                                data-testid="submit"
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                className={classes.registerButton}
                            >
                                Register
                            </StyledButton>
                        </Grid>
                        <Grid item>
                            <StyledButton
                                variant="outlined"
                                color="primary"
                                fullWidth
                                onClick={startGoogleTeacherRegister}
                                className={classes.googleButton}
                            >
                                Sign up with Google
                            </StyledButton>
                        </Grid>
                        <Grid item>
                            <Typography variant="body2" className={classes.footnote}>
                                Already have an account?
                                <Link
                                    component={RouterLink}
                                    to="/choose-user"
                                    variant="body2"
                                    className={classes.link}
                                >
                                    Sign in
                                </Link>
                            </Typography>
                        </Grid>
                    </Grid>
                </form>
            </Box>
            <InfoSnackBar
                isOpen={registerFailed}
                onClose={() => setRegisterFailed(false)}
                message={message.current}
                severity="error"
            />
        </StyledFormPanel>
    );
};
